import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useRecoilState, useRecoilValue } from 'recoil';
import ReactPlayer from '@components/library/react-player';
import { muteState, playState, routeBgmMutedState } from '@atom/setting/jukebox';
import { Ijukebox } from '@typings/setting/jukebox.interface';
import Icon from '@components/dataDisplay/Icons/Icon';
import useScrollY from '@hooks/useScrollY';

const PlayerContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NowPlayingInfo = styled.div<{ $cardColor?: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${({ $cardColor }) => $cardColor || 'rgba(0, 0, 0, 0.2)'};
  border-radius: 8px;
`;

const PageBadge = styled.div<{ $buttonColor?: string }>`
  padding: 4px 8px;
  background: ${({ $buttonColor }) => $buttonColor || 'var(--primaryColor)'};
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  color: white;
`;

const TrackInfo = styled.div`
  flex: 1;
  overflow: hidden;
`;

const TrackTitle = styled.div<{ $textColor?: string }>`
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $textColor }) => $textColor || 'inherit'};
`;

const TrackArtist = styled.div<{ $subTextColor?: string }>`
  font-size: 12px;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $subTextColor }) => $subTextColor || 'inherit'};
`;

const ControlsSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const ControlButton = styled.button<{ $size?: 'sm' | 'md' | 'lg'; $buttonColor?: string }>`
  background: ${({ $buttonColor }) => $buttonColor ? `${$buttonColor}33` : 'rgba(255, 255, 255, 0.1)'};
  border: none;
  border-radius: 50%;
  width: ${({ $size }) => ($size === 'lg' ? '48px' : $size === 'sm' ? '32px' : '40px')};
  height: ${({ $size }) => ($size === 'lg' ? '48px' : $size === 'sm' ? '32px' : '40px')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: inherit;

  &:hover {
    background: ${({ $buttonColor }) => $buttonColor ? `${$buttonColor}66` : 'rgba(255, 255, 255, 0.2)'};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ProgressBar = styled.div<{ $lineColor?: string }>`
  width: 100%;
  height: 4px;
  background: ${({ $lineColor }) => $lineColor || 'rgba(255, 255, 255, 0.2)'};
  border-radius: 2px;
  cursor: pointer;
  overflow: hidden;
`;

const Progress = styled.div<{ $progress: number; $buttonColor?: string }>`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: ${({ $buttonColor }) => $buttonColor || 'var(--primaryColor, #667eea)'};
  border-radius: 2px;
  transition: width 0.1s linear;
`;

const TimeInfo = styled.div<{ $subTextColor?: string }>`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  opacity: 0.6;
  color: ${({ $subTextColor }) => $subTextColor || 'inherit'};
`;

const HiddenPlayer = styled.div`
  display: none;
`;

const VideoPlayerWrapper = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
`;

type Props = {
  url: string;
  startY?: number;
  jukebox?: Ijukebox;
};

// YouTube URL 감지 함수
const isYouTubeUrl = (url: string): boolean => {
  return url.includes('youtube.com') || url.includes('youtu.be');
};

function PageBgmPlayer({ url, startY = 0, jukebox }: Props) {
  const showVideoPlayer = isYouTubeUrl(url);
  const [onPlay, setPlay] = useRecoilState(playState);
  const [muted, setMuted] = useRecoilState(muteState);
  // 🎵 라우트 레벨 음소거 시 재생 차단
  const isRouteBgmMuted = useRecoilValue(routeBgmMutedState);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playerReady, setPlayerReady] = useState(false);
  const hasStartedRef = useRef(false);
  const scrollY = useScrollY();

  // 스크롤 위치에 따른 재생 시작 (startY가 설정된 경우)
  useEffect(() => {
    if (!url) return;

    if (startY && startY > 0) {
      const hasScrolledPastTrigger = scrollY >= startY;

      if (hasScrolledPastTrigger && !hasStartedRef.current) {
        setPlay(true);
        document.addEventListener('click', () => setMuted(false), { once: true });
        hasStartedRef.current = true;
      }

      if (!hasScrolledPastTrigger && !hasStartedRef.current) {
        setPlay(false);
      }
    } else {
      // startY가 없으면 즉시 재생
      if (!hasStartedRef.current) {
        setPlay(true);
        document.addEventListener('click', () => setMuted(false), { once: true });
        hasStartedRef.current = true;
      }
    }
  }, [startY, scrollY, url, setPlay, setMuted]);

  // 재생/일시정지 토글
  const handlePlayPause = useCallback(() => {
    setPlay(!onPlay);
  }, [onPlay, setPlay]);

  // YouTube 진행 상태 업데이트
  const handleProgress = useCallback((state: { played: number; playedSeconds: number }) => {
    setProgress(state.played * 100);
    setCurrentTime(state.playedSeconds);
  }, []);

  // YouTube duration 설정
  const handleDuration = useCallback((dur: number) => {
    setDuration(dur);
  }, []);

  // 시간 포맷팅
  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s < 10 ? '0' + s : s}`;
  };

  // themeSet 컬러 추출
  const themeSet = jukebox?.themeSet;

  return (
    <PlayerContainer>
      {/* 현재 재생 중인 트랙 정보 */}
      <NowPlayingInfo $cardColor={themeSet?.cardColor}>
        <PageBadge $buttonColor={themeSet?.buttonColor}>
          페이지 BGM
        </PageBadge>
        <TrackInfo>
          <TrackTitle $textColor={themeSet?.textColor}>페이지 전용 음악</TrackTitle>
          <TrackArtist $subTextColor={themeSet?.subTextColor}>이 페이지를 나가면 메인 BGM으로 복귀</TrackArtist>
        </TrackInfo>
      </NowPlayingInfo>

      {/* 프로그레스바 - YouTube 플레이어가 아닐 때만 표시 */}
      {!showVideoPlayer && (
        <div>
          <ProgressBar $lineColor={themeSet?.lineColor}>
            <Progress $progress={progress} $buttonColor={themeSet?.buttonColor} />
          </ProgressBar>
          <TimeInfo $subTextColor={themeSet?.subTextColor}>
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </TimeInfo>
        </div>
      )}

      {/* 컨트롤 버튼 - YouTube 플레이어가 아닐 때만 표시 */}
      {!showVideoPlayer && (
        <ControlsSection>
          <ControlButton $size="lg" $buttonColor={themeSet?.buttonColor} onClick={handlePlayPause}>
            <Icon name={onPlay ? 'Pause20' : 'Play20'} />
          </ControlButton>
        </ControlsSection>
      )}

      {/* YouTube 플레이어 - URL에 따라 표시 여부 결정 */}
      {showVideoPlayer ? (
        <VideoPlayerWrapper>
          <ReactPlayer
            key={url}
            url={url}
            playing={onPlay && !isRouteBgmMuted}
            muted={muted}
            volume={jukebox?.defaultVolume || 0.5}
            width="100%"
            height="100%"
            loop
            onReady={() => setPlayerReady(true)}
            onProgress={handleProgress}
            onDuration={handleDuration}
            config={{
              youtube: {
                playerVars: {
                  autoplay: 1,
                  controls: 1, // YouTube 컨트롤 표시
                  loop: 1,
                  modestbranding: 1,
                },
              },
            }}
          />
        </VideoPlayerWrapper>
      ) : (
        <HiddenPlayer>
          <ReactPlayer
            key={url}
            url={url}
            playing={onPlay && !isRouteBgmMuted}
            muted={muted}
            volume={jukebox?.defaultVolume || 0.5}
            width="0"
            height="0"
            loop
            onReady={() => setPlayerReady(true)}
            onProgress={handleProgress}
            onDuration={handleDuration}
          />
        </HiddenPlayer>
      )}
    </PlayerContainer>
  );
}

export default PageBgmPlayer;
